/**
 * Мои подарки: купленные сертификаты, их статус и ссылка.
 *
 * Неактивированный подарок можно отправить снова или скопировать ссылку —
 * человек мог потерять сообщение или передумать, кому дарить.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { copyText, giftLink, sendGift, useGifts } from '@/features/gift/gift';
import type { GiftItem } from '@/shared/api/schemas';
import { formatShortDate } from '@/shared/lib/format';
import { useBackButton } from '@/shared/lib/useBackButton';
import { Screen } from '@/shared/ui/Screen';
import { Button } from '@/shared/ui/kit/Button';

import plans from '../plans/PlansPage.module.css';
import styles from './Gift.module.css';

const STATUS: Record<GiftItem['status'], { label: string; className: string }> = {
  pending: { label: 'ждёт активации', className: styles.badgePending ?? '' },
  activated: { label: 'активирован', className: styles.badgeDone ?? '' },
  cancelled: { label: 'отменён', className: styles.badgeOff ?? '' },
  refunded: { label: 'возврат', className: styles.badgeOff ?? '' },
};

const money = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

/** «3 месяца» по длительности: подарок хранит период, а не его код. */
function periodTitle(days: number | null): string {
  if (!days) return 'подписка';
  if (days >= 360) return '1 год';
  if (days < 28) {
    const t = days % 100;
    const l = days % 10;
    const unit = t >= 11 && t <= 14 ? 'дней' : l === 1 ? 'день' : l >= 2 && l <= 4 ? 'дня' : 'дней';
    return `${days} ${unit}`;
  }
  const months = Math.max(1, Math.round(days / 30));
  const tail = months % 100;
  const last = months % 10;
  const word =
    tail >= 11 && tail <= 14 ? 'месяцев' : last === 1 ? 'месяц' : last >= 2 && last <= 4 ? 'месяца' : 'месяцев';
  return `${months} ${word}`;
}

function price(gift: GiftItem): string {
  if (gift.currency === 'XTR') return `${gift.price_minor} ⭐`;
  return `${money.format(gift.price_minor / 100)} ₽`;
}

function GiftCard({ gift }: { gift: GiftItem }) {
  const [copied, setCopied] = useState(false);
  const title = `VPN на ${periodTitle(gift.duration_days)}`;
  const status = STATUS[gift.status];

  return (
    <div className={styles.item}>
      <div className={styles.itemHead}>
        <span className={styles.itemTitle}>{title}</span>
        <span className={`${styles.badge} ${status.className}`}>{status.label}</span>
      </div>
      <span className={styles.itemMeta}>
        Куплен {formatShortDate(gift.created_at)} · {price(gift)}
      </span>
      {gift.status === 'activated' ? (
        <span className={styles.itemRecipient}>
          Получил{gift.recipient ? ` ${gift.recipient}` : ''}
          {gift.activated_at ? ` · ${formatShortDate(gift.activated_at)}` : ''}
        </span>
      ) : null}
      {gift.status === 'pending' ? (
        <div className={styles.actions}>
          <Button size="s" onClick={() => void sendGift(gift, `Дарю тебе ${title}`)}>
            Отправить
          </Button>
          <Button
            size="s"
            variant="quiet"
            onClick={() => {
              void copyText(giftLink(gift.code)).then((ok) => {
                if (!ok) return;
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              });
            }}
          >
            {copied ? 'Скопировано' : 'Скопировать ссылку'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function GiftsPage() {
  const navigate = useNavigate();
  useBackButton(() => void navigate('/subscriptions'));
  const { data, isLoading, error, refetch } = useGifts();
  const items = data?.items ?? [];

  return (
    <Screen isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      <h1 className={plans.head}>Мои подарки</h1>
      {items.length === 0 && !isLoading ? (
        <p className={styles.intro}>Вы ещё никому не дарили VPN.</p>
      ) : null}
      {items.map((gift) => (
        <GiftCard key={gift.id} gift={gift} />
      ))}
      <div className={plans.pay}>
        <Button block onClick={() => void navigate('/gift')}>
          {items.length > 0 ? 'Подарить ещё' : 'Подарить другу'}
        </Button>
      </div>
    </Screen>
  );
}
